/**
 * Seo — a dependency-free document-head manager for this client-rendered SPA.
 *
 * Every route serves the same `index.html`, so without this the whole site
 * shares one <title>/description/OG set. `<Seo>` imperatively *upserts* the head
 * tags on mount and whenever its props change: it updates the tags already in
 * index.html (rather than appending duplicates) and creates the ones that are
 * missing (canonical, robots, JSON-LD). The next route's `<Seo>` overwrites the
 * same managed tags, so there is nothing to clean up on unmount.
 *
 * This is intentionally not react-helmet: for a purely client-side head we want
 * zero new dependencies and full control over de-duplication. If build-time
 * prerendering is added later, this is the one layer that would be swapped.
 */
import { useEffect } from "react";
import {
  SITE_NAME,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from "./config";

export interface SeoProps {
  title?: string;
  description?: string;
  /** Canonical path or absolute URL. Defaults to the current pathname. */
  canonical?: string;
  /** true → indexable; false → noindex,nofollow. Defaults to true. */
  index?: boolean;
  ogType?: string;
  ogImage?: string;
  /** One object or an array; serialised into a single ld+json script. */
  jsonLd?: object | object[] | null;
}

/** Find `<meta [attr]="key">` and set its content, creating it if absent. */
function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Replace the managed ld+json block (or remove it when there is none). */
function setJsonLd(data: object | object[] | null | undefined) {
  const existing = document.head.querySelector<HTMLScriptElement>('script[data-seo="ld"]');
  const blocks = data ? (Array.isArray(data) ? data : [data]) : [];
  if (blocks.length === 0) {
    if (existing) existing.remove();
    return;
  }
  const payload = blocks.length === 1 ? blocks[0] : blocks;
  const json = JSON.stringify(payload);
  if (existing) {
    existing.textContent = json;
    return;
  }
  const script = document.createElement("script");
  script.setAttribute("type", "application/ld+json");
  script.setAttribute("data-seo", "ld");
  script.textContent = json;
  document.head.appendChild(script);
}

const Seo = ({
  title,
  description,
  canonical,
  index = true,
  ogType = "website",
  ogImage,
  jsonLd,
}: SeoProps) => {
  const resolvedTitle = title ?? DEFAULT_TITLE;
  const resolvedDescription = description ?? DEFAULT_DESCRIPTION;
  const resolvedImage = ogImage ?? DEFAULT_OG_IMAGE;
  const canonicalHref = absoluteUrl(
    canonical ?? (typeof window !== "undefined" ? window.location.pathname : "/"),
  );
  const robots = index ? "index,follow" : "noindex,nofollow";

  // Serialise jsonLd for the dependency array so identical content doesn't churn.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = resolvedTitle;

    upsertMeta("name", "description", resolvedDescription);
    upsertMeta("name", "robots", robots);
    upsertLink("canonical", canonicalHref);

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:title", resolvedTitle);
    upsertMeta("property", "og:description", resolvedDescription);
    upsertMeta("property", "og:url", canonicalHref);
    upsertMeta("property", "og:image", resolvedImage);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", resolvedTitle);
    upsertMeta("name", "twitter:description", resolvedDescription);
    upsertMeta("name", "twitter:image", resolvedImage);

    setJsonLd(jsonLd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedTitle, resolvedDescription, canonicalHref, robots, ogType, resolvedImage, jsonLdKey]);

  return null;
};

export default Seo;
